import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Printer, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const ParentLetterPage = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const letterRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/admin/student/${studentId}/parent-letter-data`);
        setData(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load letter data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api, studentId]);

  const handlePrint = () => {
    const printContent = letterRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank', 'width=800,height=1100');
    if (!printWindow) {
      window.print();
      return;
    }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html><head>
        <title>Parent Letter - ${data.student_name}</title>
        <style>
          @page { size: A4; margin: 15mm; }
          body { margin: 0; padding: 0; }
        </style>
      </head><body>
        ${printContent.innerHTML}
      </body></html>
    `);
    printWindow.document.close();
    printWindow.onload = function() {
      printWindow.focus();
      printWindow.print();
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 gap-4">
        <p className="text-slate-600">Could not load student data.</p>
        <Button onClick={() => navigate('/admin/users')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Users
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* Print Controls - hidden when printing */}
      <div className="print:hidden fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-50 px-6 py-3 flex items-center justify-between shadow-sm">
        <Button variant="outline" onClick={() => navigate('/admin/users')} data-testid="back-to-users">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Users
        </Button>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">Letter for {data.student_name}</span>
          <Button onClick={handlePrint} className="bg-emerald-600 hover:bg-emerald-700" data-testid="print-letter-btn">
            <Printer className="w-4 h-4 mr-2" /> Print Letter
          </Button>
        </div>
      </div>

      {/* Letter Content */}
      <div className="print:mt-0 mt-20 flex justify-center bg-slate-100 print:bg-white min-h-screen p-8 print:p-0">
        <div
          ref={letterRef}
          className="bg-white w-[210mm] min-h-[297mm] p-[20mm] print:p-[15mm] shadow-lg print:shadow-none"
          style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", color: '#1F2937', fontSize: '11pt', lineHeight: '1.6' }}
        >
          {/* Header / Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', paddingBottom: '16px', borderBottom: '3px solid #059669' }}>
            <div style={{ width: '60px', height: '60px', borderRadius: '12px', background: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '24px', flexShrink: 0 }}>
              GM
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '22pt', fontWeight: 700, color: '#059669' }}>Growing Minds Tuition</h1>
              <p style={{ margin: 0, fontSize: '9pt', color: '#6B7280', letterSpacing: '0.5px' }}>Nurturing Potential, Building Futures</p>
            </div>
          </div>

          {/* Title */}
          <h2 style={{ textAlign: 'center', fontSize: '16pt', fontWeight: 700, color: '#059669', margin: '24px 0 20px 0' }}>
            Welcome to the Growing Minds Learning App
          </h2>

          {/* Greeting */}
          <p>Dear {data.parent_name || 'Parent/Carer'},</p>
          <p>
            We are pleased to provide your child with access to the <strong>Growing Minds Learning App</strong>.
            This will support their learning both during and outside of their tuition sessions.
          </p>
          <p>You and your child can access the app by visiting:</p>
          <div style={{ textAlign: 'center', margin: '12px 0', padding: '12px', background: '#ECFDF5', borderRadius: '8px', border: '1px solid #A7F3D0' }}>
            <strong style={{ fontSize: '14pt', color: '#059669' }}>www.growingmindslearn.co.uk</strong>
          </div>

          {/* Student Login */}
          <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '16px', margin: '20px 0' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '12pt', color: '#059669', borderBottom: '1px solid #BBF7D0', paddingBottom: '8px' }}>
              Student Login Details
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr><td style={{ padding: '4px 0', fontWeight: 600, width: '140px' }}>Student Name:</td><td>{data.student_name}</td></tr>
                <tr><td style={{ padding: '4px 0', fontWeight: 600 }}>Username:</td><td style={{ fontFamily: 'monospace', background: '#FEF9C3', padding: '2px 8px', borderRadius: '4px', display: 'inline-block' }}>{data.student_username}</td></tr>
                <tr><td style={{ padding: '4px 0', fontWeight: 600 }}>Password:</td><td style={{ fontFamily: 'monospace', background: '#FEF9C3', padding: '2px 8px', borderRadius: '4px', display: 'inline-block' }}>{data.student_password}</td></tr>
              </tbody>
            </table>
          </div>

          {/* Parent Access */}
          <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', padding: '16px', margin: '20px 0' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '12pt', color: '#1D4ED8', borderBottom: '1px solid #BFDBFE', paddingBottom: '8px' }}>
              Parent Access
            </h3>
            <p style={{ margin: '0 0 8px 0' }}>You can also monitor your child's progress using your own login.</p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr><td style={{ padding: '4px 0', fontWeight: 600, width: '140px' }}>Parent Email:</td><td style={{ fontFamily: 'monospace', background: '#FEF9C3', padding: '2px 8px', borderRadius: '4px', display: 'inline-block' }}>{data.parent_email}</td></tr>
                <tr><td style={{ padding: '4px 0', fontWeight: 600 }}>Password:</td><td style={{ fontFamily: 'monospace', background: '#FEF9C3', padding: '2px 8px', borderRadius: '4px', display: 'inline-block' }}>{data.parent_password}</td></tr>
              </tbody>
            </table>
          </div>

          {/* How the App Works */}
          <h3 style={{ fontSize: '12pt', color: '#059669', margin: '20px 0 8px 0' }}>How the App Works</h3>
          <ul style={{ paddingLeft: '20px', margin: '0 0 16px 0' }}>
            <li>Your child logs in using their details above</li>
            <li>They complete short, focused learning activities</li>
            <li>Work is tailored to their level and targets</li>
            <li>The system adapts to support progress over time</li>
            <li>Regular use builds confidence and reinforces key skills</li>
          </ul>

          {/* Monitoring Progress */}
          <h3 style={{ fontSize: '12pt', color: '#059669', margin: '20px 0 8px 0' }}>Monitoring Your Child's Progress</h3>
          <p style={{ margin: '0 0 4px 0' }}>As a parent, you can:</p>
          <ul style={{ paddingLeft: '20px', margin: '0 0 16px 0' }}>
            <li>View your child's progress and activity</li>
            <li>See strengths and areas for development</li>
            <li>Track consistency and engagement</li>
            <li>Support learning at home more effectively</li>
          </ul>

          {/* Benefits */}
          <h3 style={{ fontSize: '12pt', color: '#059669', margin: '20px 0 8px 0' }}>Benefits for Your Child</h3>
          <ul style={{ paddingLeft: '20px', margin: '0 0 16px 0' }}>
            <li>Builds confidence through regular practice</li>
            <li>Reinforces learning between sessions</li>
            <li>Helps close gaps in knowledge</li>
            <li>Encourages independent learning</li>
            <li>Makes learning engaging and rewarding</li>
            <li>Supports long-term academic progress</li>
          </ul>

          {/* Saving the App */}
          <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px', margin: '20px 0' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '11pt', color: '#374151' }}>Saving the App to Your Device</h3>
            <p style={{ margin: 0, fontSize: '10pt' }}>
              You can save the app to your phone, tablet, or computer for quick and easy access.
              When saved, the app icon will display the Growing Minds logo <strong style={{ color: '#059669' }}>GM</strong> for easy recognition.
            </p>
          </div>

          {/* Recommendation */}
          <p style={{ margin: '16px 0', fontStyle: 'italic', color: '#374151' }}>
            We recommend your child uses the app regularly, even for short periods, to maximise progress.
          </p>
          <p>If you have any questions or need support, please do not hesitate to get in touch.</p>

          {/* Sign Off */}
          <div style={{ marginTop: '24px' }}>
            <p style={{ margin: 0 }}>Kind regards,</p>
            <p style={{ margin: '4px 0 0 0', fontWeight: 700, color: '#059669' }}>Growing Minds Tuition</p>
            <p style={{ margin: '2px 0 0 0', fontSize: '10pt', color: '#6B7280' }}>Info@growingmindstuitionservices.co.uk</p>
          </div>
        </div>
      </div>

      {/* Print-specific styles */}
      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          body { margin: 0; padding: 0; }
          .print\\:hidden { display: none !important; }
          .print\\:mt-0 { margin-top: 0 !important; }
          .print\\:p-0 { padding: 0 !important; }
          .print\\:bg-white { background: white !important; }
          .print\\:shadow-none { box-shadow: none !important; }
        }
      `}</style>
    </>
  );
};

export default ParentLetterPage;
